import { QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from 'app/components/layouts/ErrorBoundary';
import { AnalyticsProvider, useAnalytics } from 'app/context/Analytics';
import useAutoOpenEffect from 'app/context/OpenState/useAutoOpenEffect';
import useOpenAnimation from 'app/context/OpenState/useOpenAnimation';
import useTailorToggleEffect from 'app/context/OpenState/useTailorToggleEffect';
import { SuggestedArticleProvider } from 'app/context/SuggestedArticleProvider';
import { PosthogEventName } from 'app/copied/constants/PosthogEventName';
import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import useFeatureFlags from 'app/hooks/useFeatureFlags';
import useModuleRedirect from 'app/hooks/useModuleRedirect';
import useRefetchOnExpiration from 'app/hooks/useRefetchOnExpiration';
import { useTailorHalfOpen } from 'app/hooks/useTailorHalfOpen';
import { useTailorIsOpen } from 'app/hooks/useTailorIsOpen';
import useBrowserNotification from 'app/modules/live-chat/hooks/useBrowserNotification';
import TailorImportantInformation from 'app/modules/tailor/components/TailorImportantInformation';
import useAimSignalMonitor from 'app/modules/tailor/hooks/useAimSignalMonitor';
import { useHistoryWriter } from 'app/modules/tailor/hooks/useHistory';
import { useSearchCleaner } from 'app/modules/tailor/hooks/useSearchCleaner';
import TailorError from 'app/modules/tailor/screens/TailorError';
import WebchatButton from 'app/modules/wrapper/WebchatButton/index';
import { routes } from 'app/routes';
import { logInfo } from 'app/services/logger';
import queryClient from 'app/services/queryClient';
import { rootContainer } from 'app/shadowRoot';
import startTwind from 'app/startTwind';
import { loadCustomJS } from 'app/utils/loadCustomJS';
import { generateUrlClass } from 'app/utils/urlClass';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { useWebchatStore } from 'app/stores/WebchatStore';
import { useExcludeTailorOnURL } from 'app/hooks/useExludeTailorOnURL';
import { useChatBubbleScrollBehavior } from 'app/hooks/useChatBubbleScrollBehavior';

import HalfOpenOverlay from './HalfOpenOverlay';
import RouterWrapper from './RouterWrapper';

function AppWrapper() {
  return (
    <QueryClientProvider client={queryClient}>
      <SuggestedArticleProvider>
        <RouterWrapper>
          <AnalyticsProvider>
            <App />
          </AnalyticsProvider>
        </RouterWrapper>
      </SuggestedArticleProvider>
    </QueryClientProvider>
  );
}

function InternalApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const element = useRoutes(routes);

  useHistoryWriter();
  useModuleRedirect();

  useEffect(() => {
    const handleRedirect = ({ detail: { to, options } }: CustomEvent) => {
      navigate(to, options);
    };

    rootContainer.addEventListener('tailor:navigate', handleRedirect as EventListener);

    return () => {
      rootContainer.removeEventListener('tailor:navigate', handleRedirect as EventListener);
    };
  }, [navigate]);

  if (!element) return null;

  return <AnimatePresence>{React.cloneElement(element, { key: location?.pathname || '' })}</AnimatePresence>;
}

function App() {
  const { isLoading } = useWebchatStore();
  const [twindStarted, setTwindStarted] = useState(false);
  const [customJSLoaded, setCustomJSLoaded] = useState(false);
  const [chatBubbleOpen, setChatBubbleOpen] = useState(true);
  const [urlClass, setUrlClass] = useState('');
  const previousUuid = useRef<string | undefined>(undefined);

  const { data, isFetched } = useFetchHelpCenter();
  const { i18n } = useTranslation();
  const flags = useFeatureFlags();
  const { isOpen, setIsOpen } = useTailorIsOpen();
  const { halfOpen } = useTailorHalfOpen();
  const { addPersistentParams, captureEventWithActiveScreen } = useAnalytics();
  const { controls } = useOpenAnimation({ halfOpen, isOpen });
  const excludeTailor = useExcludeTailorOnURL();

  useAutoOpenEffect();
  useTailorToggleEffect();
  useRefetchOnExpiration();
  useBrowserNotification();
  useSearchCleaner();
  useAimSignalMonitor();

  useEffect(() => {
    const updateUrlClass = () => {
      setUrlClass(generateUrlClass(window.location.href));
    };

    updateUrlClass();

    const observer = new MutationObserver(updateUrlClass);

    observer.observe(document, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['href'],
    });

    window.addEventListener('popstate', updateUrlClass);

    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', updateUrlClass);
    };
  }, []);

  useEffect(() => {
    if (Object.keys(flags).length) {
      addPersistentParams(flags);
    }
  }, [addPersistentParams, flags]);

  useEffect(() => {
    if (data && !customJSLoaded) {
      loadCustomJS(data);
      setCustomJSLoaded(true);
    }
  }, [data, customJSLoaded]);

  useEffect(() => {
    if (data && !twindStarted) {
      logInfo(data);
      startTwind(data, flags);
      setTwindStarted(true);
    }
  }, [data, twindStarted, flags]);

  useEffect(() => {
    const currentUuid = data?.uuid;
    const hasUuidChanged = previousUuid.current !== undefined && previousUuid.current !== currentUuid;

    if (data?.customization && hasUuidChanged) {
      logInfo('restarting twind');
      startTwind(data, flags);
      logInfo('restarting custom js');
      loadCustomJS(data);
    }

    previousUuid.current = currentUuid;
  }, [data?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (data?.customization?.custom_translations) {
      i18n.addResourceBundle(i18n.language, 'translation', data.customization.custom_translations, true, true);
    }
  }, [i18n, data?.customization?.custom_translations]);

  useEffect(() => {
    if (isFetched) {
      rootContainer.dispatchEvent(new CustomEvent('tailor:ready'));
      document.dispatchEvent(new CustomEvent('rxDefineChatReady'));
    }
  }, [isFetched]);

  const handleExpand = () => {
    if (halfOpen) {
      captureEventWithActiveScreen(PosthogEventName.HalfOpenExpanded);
    }
    captureEventWithActiveScreen(PosthogEventName.Expanded);
    setIsOpen(true);
  };

  const handleBehavior = useCallback((isOpen: boolean) => {
    setChatBubbleOpen(isOpen);
  }, []);

  useChatBubbleScrollBehavior(handleBehavior);

  if (!data || !twindStarted || isLoading || excludeTailor) return null;

  return (
    <div
      data-tailor-opened={isOpen}
      className={`fixed bottom-0 right-0 z-20 ${urlClass}`}
      style={{ pointerEvents: 'none' }}
    >
      {!isOpen && (
        <WebchatButton
          chatBubbleOpen={chatBubbleOpen}
          onClickCloseChatBubble={() => setChatBubbleOpen(false)}
        />
      )}

      {isOpen && halfOpen && <HalfOpenOverlay />}

      <motion.main
        style={{ pointerEvents: 'auto' }}
        className="flex flex-col"
        initial={{ y: window.screen.height }}
        animate={controls}
        role="dialog"
      >
        {halfOpen && (
          <div onClick={handleExpand}>
            <TailorImportantInformation
              className="-mb-4 pt-4 pb-8"
              showDescription={false}
              ctaText="Read more"
            />
          </div>
        )}
        <div
          id="ostro-widget-container"
          className="ostro-container fixed flex flex-col grow bottom-0 right-0 md:(right-10 bottom-10 h-[760px] w-[375px]) md:max-h-[calc(var(--app-height)_-_5rem)] bg-white shadow rounded-xl overflow-x-hidden relative w-app"
        >
          <AnimatePresence>
            <ErrorBoundary fallback={({ error }) => <TailorError error={error as Error} />}>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={halfOpen ? handleExpand : undefined}
                >
                  <InternalApp key={data.uuid} />
                </motion.div>
              )}
            </ErrorBoundary>
          </AnimatePresence>
        </div>
      </motion.main>
    </div>
  );
}

export default AppWrapper;
